import React, { useState, useEffect, useContext } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { UserContext } from "src/scenes/App/UserContext";
import { UserKeys } from "src/constants/userDetails";

import DirectoryTable, { FILTER_DIRECTORY, FILTER_BY } from "./components/DirectoryTable";
import ContactModal from "./components/ContactModal";
import DeleteDialog from "./components/DeleteDialog";
import SortModal from "./components/SortModal";
import CustomNameModal from "./components/CustomNameModal";
import AddListingOptionsModal from "./components/AddListingOptionsModal";
import UploadDirectoryCsvModal from "./components/UploadDirectoryCsvModal";
import DirectoryCsvPreviewModal from "./components/DirectoryCsvPreviewModal";
import { Toast, PortalPageShell, Button, BusinessPortalHeader } from "src/components";
import moment from "moment";
import { capitalizeString, customNames } from "src/utils/helpers";
import pluralize from "pluralize";
import PencilIcon from "src/img/edit@2x.png";
import { createDirectoryContacts } from "./api/directoryApi";
import { directoryCsvRowToPayload } from "./directoryCsv";

export const DefaultDirectoryFeatureName = "Directory";

const DEFAULT_FORM_STATE = {
    visibleToEmployees: false,
    visibleToClients: false,
    title: null,
    company: null,
    position: null,
    department: null,
    email: null,
    phoneNumber: null,
    address: null,
    country: null,
    website: null,
    description: null,
    imageUrl: null,
    expirationDate: null,
    customFields: []
};

export const ERROR_LIST = {
    VISIBLE_TO: { "input": "visibleTo", "message": "*You must select 1 option." },
    TITLE_OR_COMPANY: { "input": "title", "message": "*Please enter either a name or a company name." },
    EXPIRATION_DATE: { "input": "expirationDate", "message": "*Please select an option." }
};

const Directory = ({ user }) => {

    const { appFns } = useContext(UserContext);

    const [isFetching, setIsFetching] = useState(false);
    const [isFormLoading, setIsFormLoading] = useState(false);
    const [contactsArray, setContactsArray] = useState([]);

    /** @var {Array<{ uuid: string, name: string }>} companyCustomFields */
    const [companyCustomFields, setCompanyCustomFields] = useState([]);
    const [showContactModal, setShowContactModal] = useState(false);
    const [formData, setFormData] = useState(DEFAULT_FORM_STATE);
    const [updatingUuid, setUpdatingUuid] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showSortModal, setShowSortModal] = useState(false);
    const [showCustomNameModal, setShowCustomNameModal] = useState(false);
    const [showAddListingOptionsModal, setShowAddListingOptionsModal] = useState(false);
    const [showUploadDirectoryCsvModal, setShowUploadDirectoryCsvModal] = useState(false);
    const [showDirectoryCsvPreviewModal, setShowDirectoryCsvPreviewModal] = useState(false);
    const [directoryCsvRows, setDirectoryCsvRows] = useState([]);
    const [isDirectoryCsvSaving, setIsDirectoryCsvSaving] = useState(false);
    const [checkedContacts, setCheckedContacts] = useState([]);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isNoExpiration, setIsNoExpiration] = useState(false);
    const [formErrors, setFormErrors] = useState([]);
    const [filterOption, setFilterOption] = useState(FILTER_DIRECTORY.EMPLOYEE_AND_CLIENT);
    const [filterByExpiry, setFilterByExpiry] = useState(FILTER_BY.ALL_CONTACTS);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortOption, setSortOption] = useState("name");
    const [customDirectoryName, setCustomDirectoryName] = useState(user[UserKeys.CUSTOM_NAME_DIRECTORY] || DefaultDirectoryFeatureName);

    const propagateFeatureName = newName => {
        setCustomDirectoryName(newName);
        appFns.updateUserContextProperties({
            [UserKeys.CUSTOM_NAME_DIRECTORY]: newName
        });
    };

    useEffect(() => {
        fetchData();
    }, [user.token]);

    useEffect(() => {
        if (updatingUuid && !formData.expirationDate) {
            // Set checkbox to true if no expiration and updatingUuid exists
            setIsNoExpiration(true);
        }
    }, [updatingUuid, formData]);

    const fetchData = async () => {
        setIsFetching(true);
        try {
            const contactsResponse = await axios.get(`${API_URL}/businessContacts`, {
                headers: {
                    Authorization: "Bearer " + user.token
                }
            });
            setContactsArray(contactsResponse.data.contacts);
            const customFieldsResponse = await axios.get(`${API_URL}/businessContacts/customField/company`, {
                headers: {
                    Authorization: "Bearer " + user.token
                }
            });
            setCompanyCustomFields(customFieldsResponse.data.customFields);
        } catch {
            Toast.error("Unable to fetch data. Please try again later or contact support if the issue persists.");
        } finally {
            setIsFetching(false);
        }
    };

    const getContactExpiryBy = (contact) => {

        if (!contact.expirationDate) {
            return FILTER_BY.NO_EXPIRY;
        }

        //possibly will never happen as we don't allow setting of Expiry Date from current date.
        if (moment(contact.expirationDate).isBefore(moment())) {
            return FILTER_BY.EXPIRED;
        }
        
        return FILTER_BY.ALL_CONTACTS;
    };

    const changeTableFilter = (filterByUserType, filterByExpiry) => {
        // reset selected contacts (checkboxes)
        setCheckedContacts([]);

        setFilterOption(filterByUserType);
        setFilterByExpiry(filterByExpiry);
    };

    const showAddContactForm = async () => {
        setUpdatingUuid(null);
        setFormData({
            ...DEFAULT_FORM_STATE,
            customFields: companyCustomFields.map(field => ({
                uuid: field.uuid,
                name: field.name,
                value: ""
            }))
        });
        setIsNoExpiration(false);
        setFormErrors([]);
        setShowContactModal(true);
    };

    const handleAddListingRequest = () => {
        setShowAddListingOptionsModal(true);
    };

    const handleAddSingleListing = () => {
        setShowAddListingOptionsModal(false);
        showAddContactForm();
    };

    const handleOpenDirectoryCsvUpload = () => {
        setShowAddListingOptionsModal(false);
        setShowDirectoryCsvPreviewModal(false);
        setShowUploadDirectoryCsvModal(true);
    };

    const handleDirectoryCsvPreview = rows => {
        setDirectoryCsvRows(rows);
        setShowUploadDirectoryCsvModal(false);
        setShowDirectoryCsvPreviewModal(true);
    };

    const handleSaveDirectoryCsv = async rows => {
        setIsDirectoryCsvSaving(true);
        try {
            const payloads = rows.map(row => directoryCsvRowToPayload(row, companyCustomFields));
            const createdContacts = await createDirectoryContacts(user.token, payloads);
            setContactsArray(currentContacts => [...currentContacts, ...createdContacts]);
            setShowDirectoryCsvPreviewModal(false);
            setDirectoryCsvRows([]);
            setCheckedContacts([]);
            Toast.success(`${createdContacts.length} directory listings added.`);
        } catch (error) {
            const message = typeof error?.getApiErrorDetails === "function"
                ? error.getApiErrorDetails().message
                : "Unable to save the Directory CSV. Please check the listings and try again.";
            Toast.error(message);
        } finally {
            setIsDirectoryCsvSaving(false);
        }
    };

    const showEditContactForm = async (contact) => {
        try {
            setUpdatingUuid(contact.uuid);
            setFormData({
                uuid: contact.uuid,
                visibleToEmployees: contact.visibleToEmployees,
                visibleToClients: contact.visibleToClients,
                title: contact.personalName,
                company: contact.company,
                position: contact.position,
                department: contact.department,
                email: contact.email,
                phoneNumber: contact.phoneNumber,
                address: contact.address,
                country: contact.country,
                website: contact.website,
                description: contact.description,
                imageUrl: contact.imageUrl,
                expirationDate: contact.expirationDate,
                customFields: contact.customFields
            });
            setShowContactModal(true);
        } catch (e) {
            console.error("Failed to load custom field defs/values:", e);
            Toast.error("Unable to load custom field values.");
        }
    };

    const handleSaveSort = (newSort) => {
        setSortOption(newSort);
        setShowSortModal(false); 
    };


    const handleShowSortModal = () => {
        setShowSortModal(true);
    };
    
    const handleCloseSortModal = () => {
        setShowSortModal(false);
    };

    const handleShowCustomNameModal = () => {
        setShowCustomNameModal(true);
    };

    const handleCloseCustomNameModal = () => {
        setShowCustomNameModal(false);
    };

    const handleCreateOrUpdate = async (image) => {
        try {
            setIsFormLoading(true);

            // upload image
            if (image) {
                const fileExtension = image.name.split(".").pop();
                // get s3 link for image
                const s3Response = await axios.get(`${API_URL}/businessContacts/generateImageUrl/${fileExtension}`, {
                    headers: {
                        Authorization: "Bearer " + user.token
                    }
                });

                const fullUrl = s3Response.data.url;
                const storedUrl = fullUrl.substring(0, fullUrl.indexOf("?"));
                formData.imageUrl = storedUrl;

                // upload image
                await axios.put(fullUrl, image, {
                    headers: { "Content-Type": image.type }
                });
            }

            let response;

            const payload = {
                ...formData,
                customFields: formData.customFields.map(field => ({
                    companyFieldUuid: field.uuid,
                    value: field.value
                }))
            };

            if (updatingUuid) {
                response = await axios.put(`${API_URL}/businessContacts/${updatingUuid}`, payload, {
                    headers: {
                        Authorization: "Bearer " + user.token
                    }
                });

                const updatedContact = response.data.contact;
                setContactsArray(prev =>
                    prev.map(c => (c.uuid === updatingUuid ? updatedContact : c))
                );

            } else {
                response = await axios.post(`${API_URL}/businessContacts`, payload, {
                    headers: {
                        Authorization: "Bearer " + user.token
                    }
                });

                const newContact = response.data.contact;
                setContactsArray(prev => [...prev, newContact]);
            }

            const filterByDirectory = filterToShow(formData.visibleToEmployees, formData.visibleToClients);
            const contactExpiryType = getContactExpiryBy(response.data.contact);
            changeTableFilter(filterByDirectory, contactExpiryType);
            setUpdatingUuid(null);
            setIsFormLoading(false);
            setFormData(DEFAULT_FORM_STATE);
            setShowContactModal(false);
            setIsNoExpiration(false);
            setFormErrors([]);
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsFormLoading(false);
        }
    };

    const deleteCheckedContacts = async (contactUuids) => {
        try {
            await axios.delete(`${API_URL}/businessContacts`, {
                data: { contactUuids },
                headers: {
                    Authorization: "Bearer " + user.token
                }
            });
            setShowDeleteDialog(false);
            setCheckedContacts([]);
            fetchData();
        } catch {
            Toast.error("Unable to delete the selected contacts. Please try again later or contact support if the issue persists.");
        }
    };

    /** Contact Form Methods */

    const handleInputChange = e => {
        removeErrorMessage(ERROR_LIST.TITLE_OR_COMPANY.input);

        const { name, value } = e.target;
        setFormData(prevFormData => {
            return { ...prevFormData, [name]: value };
        });
    };

    const filterToShow = (isVisibleToEmployees, isVisibleToClients) => {
        if (isVisibleToEmployees && isVisibleToClients) {
            return FILTER_DIRECTORY.EMPLOYEE_AND_CLIENT;
        } else if (isVisibleToClients) {
            return FILTER_DIRECTORY.CLIENT;
        } else {
            return FILTER_DIRECTORY.EMPLOYEE;
        }
    };

    const handleVisibleTo = type => {
        removeErrorMessage(ERROR_LIST.VISIBLE_TO.input);

        if (type === "employees_and_clients") {
            setFormData({ ...formData, visibleToEmployees: true, visibleToClients: true });
        } else if (type === "employees") {
            setFormData({ ...formData, visibleToEmployees: true, visibleToClients: false });
        } else if (type === "clients") {
            setFormData({ ...formData, visibleToEmployees: false, visibleToClients: true });
        }
    };

    const handleDateCheckbox = () => {
        removeErrorMessage(ERROR_LIST.EXPIRATION_DATE.input);

        if (!isNoExpiration) {
            setFormData(prevFormData => ({
                ...prevFormData,
                expirationDate: null
            }));
        }
        setIsNoExpiration(!isNoExpiration);
    };

    const handleDateChange = date => {
        removeErrorMessage(ERROR_LIST.EXPIRATION_DATE.input);

        setFormData(prevFormData => ({
            ...prevFormData,
            expirationDate: moment(date).format("yyyy-MM-DD")
        }));
        setIsNoExpiration(false);
        setIsDatePickerOpen(false);
    };

    const handleToggleDate = () => {
        setIsDatePickerOpen(!isDatePickerOpen);
    };

    const hasFormErrors = () => {
        let newErrors = [];

        if (!formData.visibleToEmployees && !formData.visibleToClients) {
            newErrors = [...newErrors, ERROR_LIST.VISIBLE_TO];
        }
        // At least one of title or company is required
        if (!formData.title?.trim() && !formData.company?.trim()) {
            newErrors.push({ ...ERROR_LIST.TITLE_OR_COMPANY, input: "title" });
            newErrors.push({ ...ERROR_LIST.TITLE_OR_COMPANY, input: "company" });
        }
        if (!formData.expirationDate && (!isNoExpiration && !formData.expirationDate)) {
            newErrors = [...newErrors, ERROR_LIST.EXPIRATION_DATE];
        }

        setFormErrors(newErrors);
        return newErrors.length > 0;
    };

    const removeErrorMessage = key => {
        setFormErrors(prevErrors => prevErrors.filter(error => error.input !== key));
    };

    const handleCustomFieldValue = (e, fieldName) => {
        const customFields = formData.customFields.map(field =>
            field.name === fieldName ? { ...field, value: e.target.value } : field
        );

        setFormData(prevFormData => ({
            ...prevFormData,
            customFields: customFields
        }));
    };

    const handleRemoveCustomField = async (field, setRemovingUuid) => {

        const isConfirmed = window.confirm("Are you sure you want to delete this custom field? It will be removed from ALL contacts.");
        if (!isConfirmed) {
            return;
        }

        setRemovingUuid(field.uuid || field.name);

        // Delete from db if uuid exists
        if (field.uuid) {
            try {
                await axios.delete(`${API_URL}/businessContacts/customField/${field.uuid}`, {
                    headers: {
                        Authorization: "Bearer " + user.token
                    }
                });
            } catch {
                Toast.error("Unable to remove the custom field. Please try again later.");
                setRemovingUuid(null);
                return;
            }
        }

        // Remove field from formData and contactsArray if editing
        setFormData(prevFormData => {
            const updatedFields = prevFormData.customFields.filter(cf => cf.uuid !== field.uuid);
            const updatedFormData = { ...prevFormData, customFields: updatedFields };

            if (updatingUuid) {
                setContactsArray(prevContacts =>
                    prevContacts.map(contact =>
                        contact.uuid === updatingUuid
                            ? { ...contact, customFields: updatedFields }
                            : contact
                    )
                );
            }

            return updatedFormData;
        });

        // Reset loading state
        setRemovingUuid(null);
    };

    const handleCreateCustomField = async (
        name,
        setIsAddingField,
        setNewCustomFieldName,
        setIsSavingNewField
    ) => {
        if (!name.trim()) {
            // Do nothing if the name parameter is empty or only contains whitespace
            return;
        }

        try {
            setIsSavingNewField(true);
            const response = await axios.post(`${API_URL}/businessContacts/customField`, {
                "name": name
            }, {
                headers: {
                    Authorization: "Bearer " + user.token
                }
            });
            const newCustomField = response.data.customField;

            // Update the list of known company custom fields
            setCompanyCustomFields(prev => [...prev, newCustomField]);

            setFormData(prevFormData => {
                const updatedCustomFields = [...prevFormData.customFields, {
                    uuid: newCustomField.uuid,
                    name: newCustomField.name,
                    value: ""
                }];
                return {
                    ...prevFormData,
                    customFields: updatedCustomFields
                };
            });
            setIsAddingField(false);
            setNewCustomFieldName("");
        } catch {
            Toast.error("Unable to create the custom field. Please try again later or contact support if the issue persists.");
        } finally {
            setIsSavingNewField(false);
        }
    };

    return (
        <PortalPageShell>
            <BusinessPortalHeader
                title={customDirectoryName}
                description={`Create and manage business directory listings for your ${pluralize(customNames.employees.toLowerCase())} and ${pluralize(customNames.clients.toLowerCase())}.`}
            >
                <Button
                    backgroundColor="#171A22"
                    color="#FFFFFF"
                    border="1px solid #171A22"
                    height="42px"
                    borderRadius="8px"
                    padding="0 1rem"
                    gap="0.5rem"
                    onClick={handleShowCustomNameModal}
                >
                    Change Name
                    <img
                        src={PencilIcon}
                        alt=""
                        height="13px"
                        width="13px"
                    />
                </Button>
            </BusinessPortalHeader>

            <DirectoryTable
                user={user}
                contactsArray={contactsArray}
                showAddContactForm={handleAddListingRequest}
                showEditContactForm={showEditContactForm}
                showDeleteDialog={() => setShowDeleteDialog(true)}
                checkedContacts={checkedContacts}
                setCheckedContacts={setCheckedContacts}
                isFetching={isFetching}
                filterOption={filterOption}
                filterByExpiry={filterByExpiry}
                changeTableFilter={changeTableFilter}
                sortOption={sortOption}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handleShowSortModal={handleShowSortModal}
            />

            {showContactModal && (
                <ContactModal
                    hideContactModal={() => setShowContactModal(false)}
                    handleInputChange={handleInputChange}
                    handleVisibleTo={handleVisibleTo}
                    handleDateCheckbox={handleDateCheckbox}
                    handleDateChange={handleDateChange}
                    handleToggleDate={handleToggleDate}
                    isDatePickerOpen={isDatePickerOpen}
                    isNoExpiration={isNoExpiration}
                    handleCustomFieldValue={handleCustomFieldValue}
                    formData={formData}
                    setFormData={setFormData}
                    formErrors={formErrors}
                    hasFormErrors={hasFormErrors}
                    updatingUuid={updatingUuid}
                    handleCreateOrUpdate={handleCreateOrUpdate}
                    showDeleteDialog={() => setShowDeleteDialog(true)}
                    isFormLoading={isFormLoading}
                    handleRemoveCustomField={handleRemoveCustomField}
                    handleCreateCustomField={handleCreateCustomField}
                    customDirectoryName={customDirectoryName || DefaultDirectoryFeatureName}
                    user={user}
                />
            )}
            {showAddListingOptionsModal && (
                <AddListingOptionsModal
                    open={showAddListingOptionsModal}
                    directoryName={customDirectoryName || DefaultDirectoryFeatureName}
                    onClose={() => setShowAddListingOptionsModal(false)}
                    onAddSingle={handleAddSingleListing}
                    onUploadCsv={handleOpenDirectoryCsvUpload}
                />
            )}
            {showUploadDirectoryCsvModal && (
                <UploadDirectoryCsvModal
                    open={showUploadDirectoryCsvModal}
                    customFields={companyCustomFields}
                    employeeLabel={capitalizeString(pluralize(customNames.employees))}
                    clientLabel={capitalizeString(pluralize(customNames.clients))}
                    onBack={() => {
                        setShowUploadDirectoryCsvModal(false);
                        setShowAddListingOptionsModal(true);
                    }}
                    onClose={() => setShowUploadDirectoryCsvModal(false)}
                    onPreview={handleDirectoryCsvPreview}
                />
            )}
            {showDirectoryCsvPreviewModal && (
                <DirectoryCsvPreviewModal
                    open={showDirectoryCsvPreviewModal}
                    rows={directoryCsvRows}
                    customFields={companyCustomFields}
                    employeeLabel={capitalizeString(pluralize(customNames.employees))}
                    clientLabel={capitalizeString(pluralize(customNames.clients))}
                    isSaving={isDirectoryCsvSaving}
                    onBack={handleOpenDirectoryCsvUpload}
                    onClose={() => setShowDirectoryCsvPreviewModal(false)}
                    onSave={handleSaveDirectoryCsv}
                />
            )}
            {showDeleteDialog && (
                <DeleteDialog
                    closeDeleteDialog={() => setShowDeleteDialog(false)}
                    onConfirm={() => deleteCheckedContacts(checkedContacts)}
                />
            )}
            {showCustomNameModal && (
                <CustomNameModal
                    handleCloseCustomNameModal={handleCloseCustomNameModal}
                    currentCustomName={customDirectoryName}
                    setCustomDirectoryName={propagateFeatureName}
                    user={user}
                />
            )}
            {showSortModal && (
                <SortModal
                    handleCloseSortModal={handleCloseSortModal}
                    handleSaveSort={handleSaveSort}
                    sortOption={sortOption}
                    user={user}
                />

            )}
        </PortalPageShell>
    );
};

export default withRouter(Directory);
